/**
 * @module engine/history
 * @summary Undo/redo via fast shallow-cloned document snapshots.
 * @description
 *   The original used a shallow clone (5-10× faster than JSON for large
 *   diagrams). `pushUndo` is called BEFORE a mutation; `undo`/`redo` swap
 *   snapshots. DOM-coupled side-effects from the original (refreshUB updating
 *   button `disabled`, updateProps) are replaced by engine events: 'history'
 *   (undo/redo availability) and 'selection'/'doc'.
 * @ported-from JCC PFD Engineering Tool v1.1.html lines 3419-3431.
 */
import { S, emit, markDirty } from './state';
import type { DocSnapshot } from './state';
import type { ElementSpec, OperatingPoint } from '../types';
import { syncShapeCounter } from './factory';
import { scheduleAutoSave } from './persistence';

/** Shallow-clone the current shapes + connections. */
function cloneState(): DocSnapshot {
  return {
    shapes: S.shapes.map((s) => ({ ...s })),
    connections: S.connections.map((c) => ({ ...c })),
    levels: S.levels.map((l) => ({ ...l })),
    // Deep enough: the vertex list is the only nested part, and it is replaced
    // wholesale on every edit rather than mutated in place.
    pipes3: S.pipes3.map((q) => ({ ...q, pts: q.pts.map((v) => ({ ...v })) })),
    dims3: S.dims3.map((d) => ({ ...d, from: { ...d.from }, to: { ...d.to } })),
    // The recipe lines are nested and ARE edited in place (typing a percentage),
    // so they have to be copied rather than shared with the live document.
    formulations: S.formulations.map((f) => ({ ...f, lines: f.lines.map((l) => ({ ...l })) })),
    // Deep enough that editing an element in the configuration editor is
    // undoable without the snapshot aliasing the live object.
    screwConfigs: S.screwConfigs.map((c) => ({
      ...c,
      machine: { ...c.machine },
      elements: c.elements.map((e: ElementSpec) => ({ ...e, barrel: { ...e.barrel } })),
      operatingPoints: c.operatingPoints?.map((o: OperatingPoint) => ({ ...o })),
    })),
  };
}

/** Push the current state onto the undo stack (call BEFORE mutating). */
export function pushUndo(): void {
  pushCapturedUndo(cloneState());
}

/**
 * Capture a snapshot WITHOUT pushing it — for interactions that mutate live
 * (drag/resize) and only decide at pointer-up whether anything changed.
 */
export function captureSnapshot(): DocSnapshot {
  return cloneState();
}

/** Push a snapshot captured earlier (at drag start) onto the undo stack. */
export function pushCapturedUndo(snap: DocSnapshot): void {
  S.undoStack.push(snap);
  if (S.undoStack.length > 100) S.undoStack.shift();
  S.redoStack = [];
  emit('history');
  scheduleAutoSave();
}

/** Restore the previous state. */
export function undo(): void {
  if (!S.undoStack.length) return;
  S.redoStack.push(cloneState());
  const st = S.undoStack.pop()!;
  S.shapes = st.shapes;
  S.connections = st.connections;
  S.levels = st.levels || [];
  S.pipes3 = st.pipes3 || [];
  S.dims3 = st.dims3 || [];
  S.formulations = st.formulations || [];
  S.screwConfigs = st.screwConfigs || [];
  S.selected = [];
  syncShapeCounter();
  markDirty();
  emit('history');
  emit('selection');
  emit('doc');
  scheduleAutoSave();
}

/** Re-apply the next state. */
export function redo(): void {
  if (!S.redoStack.length) return;
  S.undoStack.push(cloneState());
  const st = S.redoStack.pop()!;
  S.shapes = st.shapes;
  S.connections = st.connections;
  S.levels = st.levels || [];
  S.pipes3 = st.pipes3 || [];
  S.dims3 = st.dims3 || [];
  S.formulations = st.formulations || [];
  S.screwConfigs = st.screwConfigs || [];
  S.selected = [];
  syncShapeCounter();
  markDirty();
  emit('history');
  emit('selection');
  emit('doc');
  scheduleAutoSave();
}

export function canUndo(): boolean {
  return S.undoStack.length > 0;
}
export function canRedo(): boolean {
  return S.redoStack.length > 0;
}
